import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { Subscription } from 'rxjs'
import { SeFeMenuOptions } from 'se-fe-menu'

export interface OpenRegistrationFilterOption {
  id: string
  name: string
}

export interface OpenRegistrationFilters {
  sport: string[]
  organization: string
}

type CheckboxOptionsById = Record<string, { label: string; value: boolean }[]>

@Component({
  selector: 'se-po-open-registration-filter',
  templateUrl: './open-registration-filter.component.html',
  styleUrls: ['./open-registration-filter.component.scss'],
  standalone: false,
})
export class OpenRegistrationFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public sports: OpenRegistrationFilterOption[] = []
  @Input() public organizations: OpenRegistrationFilterOption[] = []

  @Output() public filtersChange = new EventEmitter<OpenRegistrationFilters>()

  public readonly filtersForm = new UntypedFormGroup({
    sport: new UntypedFormGroup({}),
  })
  public organizationMenuOptions: SeFeMenuOptions = { sections: [] }
  public readonly sportMenuOptions: SeFeMenuOptions = {
    name: 'sportMenu',
    title: 'Sport',
    maxHeight: '50vh',
    minWidth: '180px',
  }

  public selectedSportIds: string[] = []
  public sportCheckboxOptions: CheckboxOptionsById = {}

  private selectedOrganizationId = ''
  private subscriptions = new Subscription()

  public get sportForm(): UntypedFormGroup {
    return this.filtersForm.get('sport') as UntypedFormGroup
  }

  public get sportChipText(): string {
    return this.checkboxChipText('Sport', this.sports, this.selectedSportIds)
  }

  public get organizationChipText(): string {
    return this.organizations.find(({ id }) => id === this.selectedOrganizationId)?.name || 'Organization'
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.sportForm.valueChanges.subscribe((value) => {
        this.selectedSportIds = this.selectedIdsFromControlValues(value, this.sports)
        this.emitFilters()
      })
    )
  }

  public ngOnChanges(): void {
    this.syncSelectedValuesWithOptions()
    this.buildMenu()
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  public buildMenu(): void {
    this.organizationMenuOptions = {
      name: 'organizationMenu',
      maxHeight: '50vh',
      minWidth: '200px',
      sections: [{
        menuItems: this.organizations.map((organization) => ({
          text: organization.name,
          action: () => this.selectOrganization(organization.id),
        })),
      }],
    }
  }

  public selectOrganization(organizationId: string): void {
    if (organizationId === this.selectedOrganizationId) return

    this.selectedOrganizationId = organizationId
    this.emitFilters()
  }

  public hasSelectedValues(selectedIds: string[]): boolean {
    return selectedIds.length > 0
  }

  public organizationSelected(): boolean {
    return !!this.selectedOrganizationId
  }

  private syncSelectedValuesWithOptions(): void {
    const previousSportIds = this.selectedSportIds
    const previousOrganizationId = this.selectedOrganizationId

    this.selectedSportIds = this.keepExistingSelections(this.selectedSportIds, this.sports)
    this.syncCheckboxControls(this.sportForm, this.sports, this.selectedSportIds)
    this.sportCheckboxOptions = this.toCheckboxOptions(this.sports)

    if (!this.organizations.some(({ id }) => id === this.selectedOrganizationId)) {
      this.selectedOrganizationId = ''
    }

    if (this.selectionChanged(previousSportIds, previousOrganizationId)) {
      this.emitFilters()
    }
  }

  private keepExistingSelections(selectedIds: string[], options: OpenRegistrationFilterOption[]): string[] {
    const optionIds = new Set(options.map(({ id }) => id))
    return selectedIds.filter((id) => optionIds.has(id))
  }

  private syncCheckboxControls(
    group: UntypedFormGroup,
    options: OpenRegistrationFilterOption[],
    selectedIds: string[]
  ): void {
    const optionIds = new Set(options.map(({ id }) => id))
    Object.keys(group.controls).forEach((id) => {
      if (!optionIds.has(id)) group.removeControl(id, { emitEvent: false })
    })

    options.forEach(({ id }) => {
      const selected = selectedIds.includes(id)
      if (group.contains(id)) {
        group.get(id).setValue(selected, { emitEvent: false })
      } else {
        group.addControl(id, new UntypedFormControl(selected), { emitEvent: false })
      }
    })
  }

  private selectedIdsFromControlValues(
    values: Record<string, boolean>,
    options: OpenRegistrationFilterOption[]
  ): string[] {
    return options
      .filter(({ id }) => values?.[id])
      .map(({ id }) => id)
  }

  private toCheckboxOptions(options: OpenRegistrationFilterOption[]): CheckboxOptionsById {
    return options.reduce((checkboxOptions, { id, name }) => ({
      ...checkboxOptions,
      [id]: [{ label: name, value: true }],
    }), {})
  }

  private checkboxChipText(
    defaultText: string,
    options: OpenRegistrationFilterOption[],
    selectedIds: string[]
  ): string {
    if (selectedIds.length === 1) {
      return options.find(({ id }) => id === selectedIds[0])?.name || defaultText
    }

    return selectedIds.length > 1 ? `${defaultText} (${selectedIds.length})` : defaultText
  }

  private selectionChanged(previousSportIds: string[], previousOrganizationId: string): boolean {
    return previousOrganizationId !== this.selectedOrganizationId ||
      previousSportIds.length !== this.selectedSportIds.length ||
      previousSportIds.some((id, index) => id !== this.selectedSportIds[index])
  }

  private emitFilters(): void {
    this.filtersChange.emit({
      sport: [...this.selectedSportIds],
      organization: this.selectedOrganizationId,
    })
  }
}
